import { Fragment, ReactElement } from "react";

import { Flex, FlexProps, forwardRef } from "@chakra-ui/react";

import { DropDownListItem } from "ui/DropDown/DropDownListItem";
import { TitleM } from "ui/Typography/Typography";

export type DropDownOption = {
  key: string;
  label: string;
  icon?: ReactElement<any>;
};

export type DropDownSection = {
  label: string;
  options: DropDownOption[];
};

type DropDownListProps = {
  options?: DropDownOption[];
  sections?: DropDownSection[];
  value: string | null;
  onInput: (value: string | null) => void;
  testId?: string;
  listItemStyles?: Omit<FlexProps, "onInput">;
  showScrollbar?: boolean;
} & Omit<FlexProps, "onInput">;

export const DropDownList = forwardRef<DropDownListProps, "ul">(
  (
    { options, sections, value, onInput, testId, listItemStyles, showScrollbar = false, ...rest },
    ref,
  ) => {
    const renderOption = (option: DropDownOption) => (
      <DropDownListItem
        key={option.key}
        label={option.label}
        icon={option.icon}
        isSelected={value === option.key}
        onClickItem={() => onInput(option.key)}
        dataTestId={`${testId}-option-${option.key}`}
        listItemStyles={listItemStyles}
      />
    );

    return (
      <Flex
        ref={ref}
        direction="column"
        borderRadius="sm"
        py="s100"
        bgColor="white"
        width="fit-content"
        as="ul"
        maxHeight="60vh"
        overflowY="scroll"
        overflowX="hidden"
        overscrollBehavior="none"
        style={{
          scrollbarWidth: showScrollbar ? "auto" : "none",
        }}
        css={
          showScrollbar
            ? {
                "&::-webkit-scrollbar": {
                  width: "4px",
                  height: "0px",
                },
                "&::-webkit-scrollbar-track": {
                  width: "4px",
                  height: "0px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#919191",
                  borderRadius: "8px",
                },
              }
            : {
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }
        }
        {...rest}
      >
        {sections
          ? sections.map((section) => (
              <Fragment key={section.label}>
                <TitleM color="grey.800" px="s200" py="s50">
                  {section.label}
                </TitleM>
                {section.options.map(renderOption)}
              </Fragment>
            ))
          : options?.map(renderOption)}
      </Flex>
    );
  },
);
